import { ReactNode, useEffect, useState } from "react";
import {
  MappingSystem,
  type MappingEntityType,
  type Permission,
  type ToastLevel,
} from "@observe-tech/mfe-shell-api";
import { useShellStore } from "@observe-tech/mfe-shell-api/react";
import { useShellHost } from "@/shell-handle/ShellHostProvider";

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <section className="border border-gray-200 rounded p-4 space-y-3">
      <header>
        <h3 className="text-md font-semibold">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </header>
      {children}
    </section>
  );
}

function Json({ value }: { value: unknown }) {
  return (
    <pre className="bg-gray-900 text-gray-100 p-3 rounded text-xs overflow-auto max-h-64">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

const btn =
  "px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-40";
const input = "border border-gray-300 rounded px-2 py-1 text-xs";

export function TestMfePage() {
  const host = useShellHost();
  const { handle } = host;

  const user = useShellStore(handle.auth.currentUser);
  const sites = useShellStore(handle.sites.list);
  const selectedSiteId = useShellStore(handle.sites.selectedSiteId);
  const selectedSiteIds = useShellStore(handle.sites.selectedSiteIds);
  const selectedCageIds = useShellStore(handle.sites.selectedCageIds);
  const dateRange = useShellStore(handle.dateRange.range);
  const pageMeta = useShellStore(handle.pageMeta.meta);
  const currentPath = useShellStore(handle.nav.currentPath);
  const lang = useShellStore(handle.i18n.language);
  const leftClaimed = useShellStore(handle.topbarSlots.leftClaimed);
  const centerClaimed = useShellStore(handle.topbarSlots.centerClaimed);
  const cloudSites = useShellStore(handle.cloudSites.list);
  const dashboards = useShellStore(handle.dashboards.list);
  const mappings = useShellStore(handle.mappings.list);

  const [permissions, setPermissions] = useState<Permission[]>(
    handle.permissions.raw(),
  );
  useEffect(() => {
    setPermissions(handle.permissions.raw());
    return handle.permissions.onChange(() => {
      setPermissions(handle.permissions.raw());
    });
  }, [handle]);

  const [token, setToken] = useState<string | null>(null);
  const [permCheck, setPermCheck] = useState("");
  const [navPath, setNavPath] = useState("/welcome");
  const [metaTitle, setMetaTitle] = useState("Test MFE");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [cages, setCages] = useState<unknown>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [mapId, setMapId] = useState("");
  const [mapType, setMapType] = useState<MappingEntityType>("site");
  const [mapFrom, setMapFrom] = useState(MappingSystem.OBSERVE);
  const [mapTo, setMapTo] = useState(MappingSystem.ACV4);

  const active = permissions.filter((p) => p.active);
  const inactive = permissions.filter((p) => !p.active);
  const numericSiteId =
    selectedSiteId != null ? Number(selectedSiteId) : undefined;

  const levels: ToastLevel[] = ["info", "success", "warning", "error"];

  return (
    <div className="absolute inset-0 overflow-y-auto p-6 space-y-5 text-sm">
      <header>
        <h2 className="text-lg font-semibold">Shell API harness</h2>
        <p className="text-gray-600 mt-1">
          mode: <code>{handle.mode}</code> · path: <code>{currentPath}</code>
        </p>
      </header>

      <Section title="auth" subtitle="auth.currentUser · auth.getToken()">
        <Json value={user} />
        <div className="flex items-center gap-2">
          <button
            className={btn}
            onClick={async () => {
              setBusy("token");
              try {
                setToken(await handle.auth.getToken());
              } catch (e) {
                handle.notify(
                  e instanceof Error ? e.message : "getToken failed",
                  "error",
                );
              } finally {
                setBusy(null);
              }
            }}
            disabled={busy === "token"}
          >
            getToken()
          </button>
          {token && (
            <code className="text-xs break-all text-gray-600">
              {token.slice(0, 24)}…{token.slice(-12)}
            </code>
          )}
        </div>
      </Section>

      <Section
        title="notify"
        subtitle="handle.notify(msg, level) → shared coralreef toast"
      >
        <div className="flex flex-wrap gap-2">
          {levels.map((level) => (
            <button
              key={level}
              className={btn}
              onClick={() =>
                handle.notify(`Test ${level} toast from shell harness`, level)
              }
            >
              {level}
            </button>
          ))}
        </div>
      </Section>

      <Section
        title="permissions"
        subtitle={`permissions.raw() · ${permissions.length} total · ${active.length} active · ${inactive.length} inactive`}
      >
        <div className="flex items-center gap-2">
          <input
            className={input}
            placeholder="permission name"
            value={permCheck}
            onChange={(e) => setPermCheck(e.target.value)}
          />
          <span className="text-xs text-gray-600">
            has(): <code>{String(handle.permissions.has(permCheck))}</code>
          </span>
        </div>
        {permissions.length === 0 ? (
          <p className="text-gray-500 text-xs">No permissions loaded.</p>
        ) : (
          <table className="w-full border border-gray-200 text-left">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-1 border-b">Name</th>
                <th className="px-3 py-1 border-b">Active</th>
                <th className="px-3 py-1 border-b">Sites</th>
              </tr>
            </thead>
            <tbody>
              {permissions.map((p) => (
                <tr key={p.name} className={p.active ? "" : "text-gray-400"}>
                  <td className="px-3 py-1 border-b font-mono text-xs">
                    {p.name}
                  </td>
                  <td className="px-3 py-1 border-b">
                    {p.active ? "yes" : "no"}
                  </td>
                  <td className="px-3 py-1 border-b font-mono text-xs">
                    {p.sites.length === 0 ? "(none)" : p.sites.join(", ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title="sites"
        subtitle="sites.list · selectedSiteId · selectedSiteIds · selectedCageIds"
      >
        <div className="flex flex-wrap items-center gap-2">
          <select
            className={input}
            value={selectedSiteId ?? ""}
            onChange={(e) => handle.sites.setSelectedSite(e.target.value)}
          >
            <option value="">(select site)</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.id})
              </option>
            ))}
          </select>
          <button className={btn} onClick={() => handle.sites.refresh()}>
            refresh()
          </button>
          <button
            className={btn}
            onClick={() =>
              handle.sites.setSelectedSiteIds(
                numericSiteId != null ? [numericSiteId] : [],
              )
            }
          >
            setSelectedSiteIds([selected])
          </button>
          <button
            className={btn}
            onClick={() => handle.sites.setSelectedCageIds([])}
          >
            clear cageIds
          </button>
        </div>
        <Json
          value={{
            selectedSiteId,
            selectedSiteIds,
            selectedCageIds,
            sitesCount: sites.length,
          }}
        />
      </Section>

      <Section
        title="cloudSites (local servers)"
        subtitle="cloudSites.list · machines = on-site local servers + marlinVersion"
      >
        <button className={btn} onClick={() => handle.cloudSites.refresh()}>
          refresh()
        </button>
        {cloudSites.length === 0 ? (
          <p className="text-gray-500 text-xs">
            No cloud sites loaded. Click refresh.
          </p>
        ) : (
          <table className="w-full border border-gray-200 text-left">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-1 border-b">Site</th>
                <th className="px-3 py-1 border-b">Local servers (machines)</th>
                <th className="px-3 py-1 border-b">Cages</th>
              </tr>
            </thead>
            <tbody>
              {cloudSites.map((s) => (
                <tr key={s.id}>
                  <td className="px-3 py-1 border-b">
                    {s.name} ({s.id})
                  </td>
                  <td className="px-3 py-1 border-b font-mono text-xs">
                    {s.machines.length === 0
                      ? "(none)"
                      : s.machines
                          .map((m) => `#${m.id}@${m.marlinVersion}`)
                          .join(", ")}
                  </td>
                  <td className="px-3 py-1 border-b">{s.cages.length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title="Kraken Mappings"
        subtitle={`mappings.list · getMapping(from, to) · ${mappings.length} tenants · ${mappings.reduce((n, t) => n + t.sites.length, 0)} sites`}
      >
        <button className={btn} onClick={() => handle.mappings.refresh()}>
          refresh()
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <input
            className={input}
            placeholder="id"
            value={mapId}
            onChange={(e) => setMapId(e.target.value)}
          />
          <select
            className={input}
            value={mapType}
            onChange={(e) => setMapType(e.target.value as MappingEntityType)}
          >
            <option value="tenant">tenant</option>
            <option value="site">site</option>
            <option value="cage">cage</option>
          </select>
          <select
            className={input}
            value={mapFrom}
            onChange={(e) => setMapFrom(e.target.value as MappingSystem)}
          >
            {Object.entries(MappingSystem).map(([k, v]) => (
              <option key={v} value={v}>
                from {k}
              </option>
            ))}
          </select>
          <select
            className={input}
            value={mapTo}
            onChange={(e) => setMapTo(e.target.value as MappingSystem)}
          >
            {Object.entries(MappingSystem).map(([k, v]) => (
              <option key={v} value={v}>
                to {k}
              </option>
            ))}
          </select>
          <span className="text-xs text-gray-600">
            getMapping():{" "}
            <code>
              {JSON.stringify(
                handle.mappings.getMapping(
                  { id: mapId, system: mapFrom, type: mapType },
                  mapTo,
                ),
              )}
            </code>
          </span>
        </div>
        <Json value={mappings} />
      </Section>

      <Section title="dashboards" subtitle="dashboards.refresh(siteId)">
        <button
          className={btn}
          onClick={() =>
            numericSiteId != null && handle.dashboards.refresh(numericSiteId)
          }
          disabled={numericSiteId == null}
        >
          refresh(selectedSiteId)
        </button>
        <Json value={dashboards} />
      </Section>

      <Section title="cages" subtitle="cages.listForSite / refresh(siteId)">
        <button
          className={btn}
          disabled={selectedSiteId == null}
          onClick={async () => {
            if (selectedSiteId == null) return;
            await handle.cages.refresh(selectedSiteId);
            setCages(handle.cages.listForSite(selectedSiteId).get());
          }}
        >
          refresh + list(selectedSiteId)
        </button>
        <Json value={cages} />
      </Section>

      <Section title="dateRange" subtitle="dateRange.range / setRange()">
        <div className="flex flex-wrap items-center gap-2">
          <input
            className={input}
            placeholder="start (ISO)"
            value={rangeStart}
            onChange={(e) => setRangeStart(e.target.value)}
          />
          <input
            className={input}
            placeholder="end (ISO)"
            value={rangeEnd}
            onChange={(e) => setRangeEnd(e.target.value)}
          />
          <button
            className={btn}
            onClick={() =>
              handle.dateRange.setRange(rangeStart || null, rangeEnd || null)
            }
          >
            setRange()
          </button>
        </div>
        <Json value={dateRange} />
      </Section>

      <Section title="pageMeta" subtitle="pageMeta.meta / set() / clear()">
        <div className="flex flex-wrap items-center gap-2">
          <input
            className={input}
            value={metaTitle}
            onChange={(e) => setMetaTitle(e.target.value)}
          />
          <button
            className={btn}
            onClick={() => handle.pageMeta.set({ title: metaTitle })}
          >
            set title
          </button>
          <button className={btn} onClick={() => handle.pageMeta.clear()}>
            clear()
          </button>
        </div>
        <Json value={pageMeta} />
      </Section>

      <Section title="nav" subtitle="nav.currentPath / navigate() / back()">
        <div className="flex flex-wrap items-center gap-2">
          <input
            className={input}
            value={navPath}
            onChange={(e) => setNavPath(e.target.value)}
          />
          <button className={btn} onClick={() => handle.nav.navigate(navPath)}>
            navigate()
          </button>
          <button className={btn} onClick={() => handle.nav.back()}>
            back()
          </button>
        </div>
      </Section>

      <Section title="i18n" subtitle="i18n.language / setLanguage()">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs">
            current: <code>{lang}</code>
          </span>
          {["en", "no", "es"].map((l) => (
            <button
              key={l}
              className={btn}
              onClick={() => handle.i18n.setLanguage(l)}
            >
              {l}
            </button>
          ))}
        </div>
      </Section>

      <Section
        title="topbarSlots"
        subtitle="leftClaimed / centerClaimed (MFE-owned flags)"
      >
        <Json value={{ leftClaimed, centerClaimed }} />
      </Section>
    </div>
  );
}
